// src/modules/clinical-notes-v2/services/clinicalNotesAiService.js
//
// Wrapper de los 5 endpoints del backend AI Clinical Notes
// (/api/ClinicalNotesAi/*). Contrato implementado en
// hipaa-logs/Docs/Control/18_ENDPOINTS_AI_CLINICAL_NOTES_BACKEND.md.
//
// Convenciones del módulo:
// - `templateName` AI ≠ AdvancedMD para 2 strings (ver tablas abajo).
// - PHI policy del proyecto: el `clinicalContext.patient` se sanitiza
//   antes de cualquier llamada AI — solo viaja age/gender/memberType
//   (más cipId como identificador interno). NO se envía fullName ni dob.
// - Rate limit `ai-clinical-notes` = 30 req / 10 min por empleado,
//   compartido entre suggest/care-gap, suggest/coding-opportunity y
//   draft-note. El 429 se convierte en `RateLimitError`.

import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";
import { callOpenAIChat } from "../../../utils/services/openaiClient";
import phiSanitizer from "../../../utils/security/phiSanitizer";

const API_URL = `${API_BASE_URL}/api/ClinicalNotesAi`;

// =============================================================
// Templates
// =============================================================

/**
 * AdvancedMD templateName -> AI templateName.
 * Usar antes de llamar Eps 2/3/4.
 */
export const AI_TEMPLATE_NAMES = Object.freeze({
  "Follow Up Exam 2.0":            "Follow Up Exam 2.0",
  "Hospital Discharge Follow Up":  "Hospital Discharge FU",
  "Annual Wellness 2.0":           "Annual Wellness 2.0",
  "Telehealth Visit 2.0":          "Telehealth 2.0",
});

/**
 * AI templateName -> AdvancedMD templateName.
 * Usar después del Ep 4 (draft-note), antes de pasar el draft al
 * `buildEhrNotePayload` del módulo legacy.
 */
export const ADVMD_TEMPLATE_NAMES = Object.freeze(
  Object.fromEntries(
    Object.entries(AI_TEMPLATE_NAMES).map(([advmd, ai]) => [ai, advmd])
  )
);

/**
 * Convierte el nombre del template del lado AdvancedMD al string que
 * espera el backend AI. Si recibe uno desconocido lo devuelve sin
 * cambios (mejor enviar algo que romper la llamada).
 */
export const toAiTemplateName = (name) => AI_TEMPLATE_NAMES[name] || name;

/**
 * Convierte el nombre del template del lado AI al string que usa
 * AdvancedMD (legacy `buildEhrNotePayload`).
 */
export const toAdvmdTemplateName = (name) => ADVMD_TEMPLATE_NAMES[name] || name;

// =============================================================
// PHI sanitization
// =============================================================

/**
 * Sanitiza el bloque `patient` antes de enviarlo a la IA.
 *
 * Política del proyecto (decidida en doc 17 §0 #10):
 * solo age, gender, memberType y cipId. Cualquier otro campo (fullName,
 * dob, pcpName, locationId, etc.) se descarta para reducir la PHI que
 * viaja al prompt.
 *
 * @param {Object|null|undefined} patient
 * @returns {{cipId?: string, age?: number, gender?: string, memberType?: string}}
 */
export function sanitizePatientForAi(patient) {
  if (!patient || typeof patient !== "object") return {};
  const out = {};
  if (patient.cipId)        out.cipId      = patient.cipId;
  if (patient.age != null)  out.age        = patient.age;
  if (patient.gender)       out.gender     = patient.gender;
  if (patient.memberType)   out.memberType = patient.memberType;
  return out;
}

/**
 * Aplica `sanitizePatientForAi` dentro de un `clinicalContext` antes de
 * enviarlo a Eps 2/3/4. NO muta el bundle original.
 */
export function sanitizeClinicalContext(ctx) {
  if (!ctx || typeof ctx !== "object") return ctx;
  return { ...ctx, patient: sanitizePatientForAi(ctx.patient) };
}

// =============================================================
// Trace cleanup
// =============================================================

/**
 * Quita el campo `_aiTrace` (debug/audit del lado AI) del draft antes de
 * pasarlo al pipeline de AdvancedMD. El backend setea `_aiTrace` y lo
 * marca explícitamente como "opcional para el front; NO enviar a AdvancedMD".
 *
 * @param {Object} draft - respuesta de POST /draft-note
 * @returns {Object} mismo shape sin `_aiTrace`
 */
export function stripAiTrace(draft) {
  if (!draft || typeof draft !== "object") return draft;
  const { _aiTrace, ...rest } = draft;
  return rest;
}

// =============================================================
// Errors
// =============================================================

/**
 * Lanzado cuando el backend devuelve 429 sobre la política
 * `ai-clinical-notes`. El caller debería mostrar un Snackbar pidiendo
 * esperar (ventana de 10 min).
 */
export class RateLimitError extends Error {
  constructor(message = "Rate limit exceeded for AI endpoint. Retry later.") {
    super(message);
    this.name = "RateLimitError";
    this.status = 429;
  }
}

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

/**
 * Convierte cualquier error de axios en `RateLimitError` si es 429,
 * o re-throwea el original. Centraliza la lectura del mensaje del
 * backend (puede venir como `{message}` o `{result:1, message}`).
 */
function normalizeError(err) {
  if (err?.response?.status === 429) {
    const msg = err?.response?.data?.message || err?.response?.data || undefined;
    throw new RateLimitError(typeof msg === "string" ? msg : undefined);
  }
  throw err;
}

// =============================================================
// JSON parsing helper (idéntico al usado por treatmentPlanAiService /
// HCCAi / HedisAi — el modelo a veces envuelve la respuesta en fences
// markdown ```json ... ``` o agrega prosa antes/después)
// =============================================================

function safeParseJsonFromModel(rawContent) {
  if (!rawContent) throw new Error("No content to parse");
  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) => (typeof p === "string" ? p : (p && (p.text || p.content || "")) || ""))
        .join("")
    : String(rawContent);
  text = text.trim();
  if (text.startsWith("```")) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, "").replace(/```$/m, "").trim();
  }
  const firstBrace = text.indexOf("{");
  const lastBrace  = text.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    throw new Error("No JSON object found in model response");
  }
  return JSON.parse(text.slice(firstBrace, lastBrace + 1));
}

// =============================================================
// Prompt builders — el front arma el prompt; el proxy
// /api/AI/chat/completions solo reenvía a OpenAI con la key del backend.
// =============================================================

const SYSTEM_PROMPT_CARE_GAP = `You are a clinical decision support assistant for HEDIS care gap closure.
Generate a concise, clinically actionable action plan that the physician can document
inside a US-style clinical note to close the specified care gap.

ALL OUTPUT MUST BE IN ENGLISH.
DO NOT prescribe exact drug doses — use language like "consider starting", "optimize current therapy", "evaluate adjustment".
Return ONLY a JSON object, no prose around it, no markdown fences.`;

const SYSTEM_PROMPT_CODING_OP = `You are a clinical decision support assistant for HCC coding accuracy and M.E.A.T. documentation.
Generate a concise plan of action a physician can use to support an HCC coding opportunity
during a US-style clinical note, including which exams/labs to order and how to document
under M.E.A.T. (Monitor, Evaluate, Assess, Treat).

ALL OUTPUT MUST BE IN ENGLISH.
DO NOT prescribe exact drug doses.
Return ONLY a JSON object, no prose around it, no markdown fences.`;

function renderClinicalContext(ctx, extraContext) {
  const c = sanitizeClinicalContext(ctx) || {};
  const lines = [];
  if (c.patient) {
    const p = c.patient;
    lines.push(`Patient: ${[
      p.age ? `${p.age}y` : null,
      p.gender || null,
      p.memberType ? `member type ${p.memberType}` : null,
    ].filter(Boolean).join(", ") || "—"}`);
  }
  if (Array.isArray(c.chronicConditions) && c.chronicConditions.length) {
    lines.push(`Chronic conditions: ${c.chronicConditions.map((x) => `${x.description || ""}${x.icd10 ? ` (${x.icd10})` : ""}`).filter(Boolean).join("; ")}`);
  }
  if (Array.isArray(c.activeMedications) && c.activeMedications.length) {
    lines.push(`Active medications:\n${c.activeMedications.slice(0, 20).map((m) => `  - ${m.name}${m.dose ? ` — ${m.dose}` : ""}`).join("\n")}`);
  }
  if (Array.isArray(c.allergies) && c.allergies.length) {
    lines.push(`Allergies: ${c.allergies.map((a) => `${a.substance}${a.reaction ? ` (${a.reaction})` : ""}`).join("; ")}`);
  }
  if (Array.isArray(c.recentLabs) && c.recentLabs.length) {
    lines.push(`Recent labs:\n${c.recentLabs.slice(0, 12).map((l) => `  - ${l.date || "—"} ${l.name}: ${l.value}${l.unit ? " " + l.unit : ""}${l.abnormalFlag ? ` [${l.abnormalFlag}]` : ""}${l.referenceRange ? ` (ref ${l.referenceRange})` : ""}`).join("\n")}`);
  }
  if (Array.isArray(c.recentVitals) && c.recentVitals.length) {
    lines.push(`Recent vitals: ${c.recentVitals.slice(0, 3).map((v) => `${v.date || "—"}: BP ${v.bpSys || "?"}/${v.bpDia || "?"}, HR ${v.pulse || "?"}, BMI ${v.bmi || "?"}`).join(" | ")}`);
  }
  if (extraContext) {
    // HIPAA 45 CFR 164.514: el texto libre del médico puede contener
    // identificadores (nombres, teléfonos, fechas) — sanitizar antes del prompt
    lines.push(`Physician notes for the AI: ${phiSanitizer.sanitizeText(extraContext)}`);
  }
  return lines.join("\n");
}

function buildCareGapPrompt({ gap, templateName, extraContext, clinicalContext }) {
  return `Note template: ${templateName}

Care gap to close:
  - Measure: ${gap?.measure || "—"}
  - ICD-10:  ${gap?.icd10 || "—"}
  - QM:      ${gap?.qm || "—"}
  - Open since: ${gap?.openSince || "—"}

${renderClinicalContext(clinicalContext, extraContext)}

Return ONLY this JSON shape:
{
  "hpi": "Short HPI narrative the physician can paste into the History of Present Illness section. 2-4 sentences.",
  "examFindings": "What to document in the exam to support gap closure.",
  "assessment": "Assessment paragraph mentioning the gap by name + relevant ICD-10.",
  "plan": ["Plan step 1", "Plan step 2", "..."],
  "followUp": "Follow-up text including timing.",
  "documentationCues": ["What MUST be documented for the gap to be considered closed", "..."],
  "icd10Codes": ["E11.9", "..."],
  "cptCodes": ["S0625", "..."]
}`;
}

function buildCodingOpPrompt({ op, templateName, extraContext, clinicalContext }) {
  const evidenceList = Array.isArray(op?.evidence) && op.evidence.length
    ? op.evidence.map((e) => `${e.source || ""}: ${e.ref || ""}`).join("; ")
    : "—";

  return `Note template: ${templateName}

Coding opportunity:
  - Suspected ICD-10: ${op?.suspectedIcd10 || "—"}
  - Description:     ${op?.suspectedDescription || "—"}
  - HCC category:    ${op?.hccCategory || "—"}
  - RAF impact:      ${op?.rafImpact != null ? `+${Number(op.rafImpact).toFixed(3)}` : "—"}
  - Existing evidence: ${evidenceList}

${renderClinicalContext(clinicalContext, extraContext)}

Return ONLY this JSON shape:
{
  "examsRecommended": [
    { "type": "lab" | "imaging" | "ecg" | "other", "name": "...", "rationale": "..." }
  ],
  "meatDocumentation": {
    "monitor":  "What to monitor at each visit",
    "evaluate": "What to evaluate / order",
    "assess":   "Specific assessment language including correct ICD-10 specificity",
    "treat":    "Treatment direction (general — no exact drug doses)"
  },
  "hpiAddition": "HPI addition the physician can paste into HPI to support this code.",
  "assessment": "Assessment paragraph supporting the suspected diagnosis.",
  "plan": ["Plan step 1", "..."],
  "icd10Specificity": "Explanation of what specificity is needed for accurate HCC coding (e.g. EF for HF type)."
}`;
}

function approximateAiMeta(usage, model) {
  return {
    model: model || "gpt-4o-mini",
    promptTokens:     usage?.prompt_tokens     ?? null,
    completionTokens: usage?.completion_tokens ?? null,
    latencyMs: null,
    cached: false,
  };
}

/**
 * Detecta el "429" dentro del Error genérico que tira openaiClient
 * (formato: "AI Proxy error: 429") y lo convierte en RateLimitError.
 */
function normalizeProxyError(err) {
  const msg = err?.message || "";
  if (/AI Proxy error:\s*429/.test(msg)) {
    throw new RateLimitError();
  }
  throw err;
}

// =============================================================
// Endpoint 1 — GET /patient-context/{cipId}
// =============================================================

/**
 * Bundle clínico liviano del paciente: solo `patient` + `openCareGaps` +
 * `openCodingOpportunities`. Las 5 secciones clínicas (chronicConditions,
 * activeMedications, allergies, recentVitals, recentLabs) salen `[]`
 * por diseño — el front las arma localmente y las envía dentro de
 * `clinicalContext` en Eps 2/3/4.
 *
 * Ejemplo de request:
 * GET /api/ClinicalNotesAi/patient-context/12345?careGapStatus=53&codingOpStatus=1
 *
 * Respuesta exitosa (200) — shape resumido:
 * {
 *   "patient": {
 *     "cipId": "12345", "advancedMdPatientId": null,
 *     "fullName": "John Doe", "dob": "1958-03-12", "age": 67,
 *     "gender": "M", "pcpName": "Dr. Smith",
 *     "locationId": 2, "locationName": "Palm Springs"
 *   },
 *   "chronicConditions": [], "activeMedications": [], "allergies": [],
 *   "recentVitals": [], "recentLabs": [],
 *   "openCareGaps": [
 *     { "careGapId": 9876, "qm": null, "measure": "...", "icd10": null, ... }
 *   ],
 *   "openCodingOpportunities": [
 *     { "codingOpportunityId": 5432, "suspectedIcd10": "I50.9",
 *       "suspectedDescription": "Heart failure, unspecified",
 *       "hccCategory": "HCC85", "rafImpact": null, "evidence": [], "status": "Open" }
 *   ]
 * }
 *
 * Errores: 401/403 si falta JWT, 500 si el SP subyacente falla.
 *
 * @param {string} cipId
 * @param {Object} [opts]
 * @param {number} [opts.careGapStatus=53] - filtro StatusId en PatientCareGapItem
 * @param {number} [opts.codingOpStatus=1] - 1 = Open
 * @param {number} [opts.labsLimit=10]  - reservado (no se usa hoy)
 * @param {number} [opts.vitalsLimit=3] - reservado (no se usa hoy)
 */
export async function getPatientContext(cipId, opts = {}) {
  if (!cipId) throw new Error("cipId is required");
  try {
    const res = await axios.get(`${API_URL}/patient-context/${cipId}`, {
      params: {
        careGapStatus: opts.careGapStatus ?? 53,
        codingOpStatus: opts.codingOpStatus ?? 1,
        labsLimit:  opts.labsLimit  ?? 10,
        vitalsLimit: opts.vitalsLimit ?? 3,
      },
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    normalizeError(err);
  }
}

// =============================================================
// Endpoint 2 — POST /suggest/care-gap
// =============================================================

/**
 * Plan de acción IA para abordar UN care gap dentro de la nota clínica.
 *
 * IMPLEMENTACIÓN v1.39.2: usa el proxy genérico `/api/AI/chat/completions`
 * (vía `callOpenAIChat`) en vez del endpoint dedicado del backend
 * `/api/ClinicalNotesAi/suggest/care-gap`. Reutiliza la misma
 * infraestructura que RAF Score, Treatment Plan, HCC, HEDIS, etc.
 *
 * El front construye el prompt localmente (sanitiza el PHI antes de
 * enviarlo). La forma de la respuesta se mantiene IDÉNTICA a la versión
 * anterior para que las cards/panels no requieran cambios.
 *
 * @param {Object} args
 * @param {string} args.cipId               (kept para audit/logging del caller)
 * @param {number} args.careGapId
 * @param {string} args.templateName
 * @param {string} [args.extraContext]
 * @param {Object} [args.clinicalContext]   - bundle armado por el front
 * @param {Object} [args.gap]               - el item completo del care gap (measure, icd10, qm, openSince)
 * @param {string} [args.model]
 *
 * Respuesta:
 * {
 *   careGapId, measure,
 *   suggestion: { hpi, examFindings, assessment, plan[], followUp,
 *                 documentationCues[], icd10Codes[], cptCodes[] },
 *   aiMeta: { model, promptTokens, completionTokens, latencyMs, cached }
 * }
 *
 * Errores: lanza `RateLimitError` si el proxy responde 429; otro error
 * cualquiera se propaga al caller (snackbar genérico).
 */
export async function suggestCareGap({
  cipId,
  careGapId,
  templateName,
  extraContext,
  clinicalContext,
  gap,
  model,
}) {
  if (!cipId) throw new Error("cipId is required");
  if (!careGapId || careGapId <= 0) throw new Error("careGapId is required");
  if (!templateName) throw new Error("templateName is required");

  const gapForPrompt = gap
    || (clinicalContext?.openCareGaps || []).find((g) => g.careGapId === careGapId)
    || { careGapId };

  const userPrompt = buildCareGapPrompt({
    gap: gapForPrompt,
    templateName: toAiTemplateName(templateName),
    extraContext,
    clinicalContext,
  });

  const t0 = performance.now();
  let data;
  try {
    data = await callOpenAIChat({
      model: model || "gpt-4o-mini",
      messages: [
        { role: "system", content: SYSTEM_PROMPT_CARE_GAP },
        { role: "user",   content: userPrompt },
      ],
      temperature: 0.2,
    });
  } catch (err) {
    normalizeProxyError(err);
  }
  const latencyMs = Math.round(performance.now() - t0);

  const content = data?.choices?.[0]?.message?.content;
  let parsed;
  try {
    parsed = safeParseJsonFromModel(content);
  } catch (err) {
    // HIPAA: no loguear `content` (respuesta clínica del modelo)
    console.error("[suggestCareGap] JSON parse failed:", err.message);
    throw new Error("AI response was not valid JSON. Try again.");
  }

  return {
    careGapId,
    measure: gapForPrompt.measure || null,
    suggestion: {
      hpi:               typeof parsed.hpi === "string"          ? parsed.hpi          : "",
      examFindings:      typeof parsed.examFindings === "string" ? parsed.examFindings : "",
      assessment:        typeof parsed.assessment === "string"   ? parsed.assessment   : "",
      plan:              Array.isArray(parsed.plan)              ? parsed.plan         : [],
      followUp:          typeof parsed.followUp === "string"     ? parsed.followUp     : "",
      documentationCues: Array.isArray(parsed.documentationCues) ? parsed.documentationCues : [],
      icd10Codes:        Array.isArray(parsed.icd10Codes)        ? parsed.icd10Codes   : [],
      cptCodes:          Array.isArray(parsed.cptCodes)          ? parsed.cptCodes     : [],
    },
    aiMeta: {
      ...approximateAiMeta(data?.usage, data?.model || model),
      latencyMs,
    },
  };
}

// =============================================================
// Endpoint 3 — POST /suggest/coding-opportunity
// =============================================================

/**
 * Plan de acción IA con exámenes recomendados + M.E.A.T. para UNA coding
 * opportunity.
 *
 * IMPLEMENTACIÓN v1.39.2: usa el proxy genérico `/api/AI/chat/completions`
 * en vez del endpoint dedicado `/api/ClinicalNotesAi/suggest/coding-opportunity`.
 * Mismo patrón que `suggestCareGap`.
 *
 * @param {Object} args
 * @param {string} args.cipId
 * @param {number} args.codingOpportunityId
 * @param {string} args.templateName
 * @param {string} [args.extraContext]
 * @param {Object} [args.clinicalContext]
 * @param {Object} [args.op]                - el item completo del coding op
 * @param {string} [args.model]
 *
 * Respuesta:
 * {
 *   codingOpportunityId, suspectedIcd10, suspectedDescription,
 *   hccCategory, rafImpact,
 *   suggestion: { examsRecommended[], meatDocumentation{}, hpiAddition,
 *                 assessment, plan[], icd10Specificity },
 *   aiMeta: { ... }
 * }
 */
export async function suggestCodingOpportunity({
  cipId,
  codingOpportunityId,
  templateName,
  extraContext,
  clinicalContext,
  op,
  model,
}) {
  if (!cipId) throw new Error("cipId is required");
  if (!codingOpportunityId || codingOpportunityId <= 0)
    throw new Error("codingOpportunityId is required");
  if (!templateName) throw new Error("templateName is required");

  const opForPrompt = op
    || (clinicalContext?.openCodingOpportunities || []).find((o) => o.codingOpportunityId === codingOpportunityId)
    || { codingOpportunityId };

  const userPrompt = buildCodingOpPrompt({
    op: opForPrompt,
    templateName: toAiTemplateName(templateName),
    extraContext,
    clinicalContext,
  });

  const t0 = performance.now();
  let data;
  try {
    data = await callOpenAIChat({
      model: model || "gpt-4o-mini",
      messages: [
        { role: "system", content: SYSTEM_PROMPT_CODING_OP },
        { role: "user",   content: userPrompt },
      ],
      temperature: 0.2,
    });
  } catch (err) {
    normalizeProxyError(err);
  }
  const latencyMs = Math.round(performance.now() - t0);

  const content = data?.choices?.[0]?.message?.content;
  let parsed;
  try {
    parsed = safeParseJsonFromModel(content);
  } catch (err) {
    // HIPAA: no loguear `content` (respuesta clínica del modelo)
    console.error("[suggestCodingOpportunity] JSON parse failed:", err.message);
    throw new Error("AI response was not valid JSON. Try again.");
  }

  return {
    codingOpportunityId,
    suspectedIcd10:        opForPrompt.suspectedIcd10 || null,
    suspectedDescription:  opForPrompt.suspectedDescription || null,
    hccCategory:           opForPrompt.hccCategory || null,
    rafImpact:             opForPrompt.rafImpact ?? null,
    suggestion: {
      examsRecommended:  Array.isArray(parsed.examsRecommended) ? parsed.examsRecommended : [],
      meatDocumentation: parsed.meatDocumentation && typeof parsed.meatDocumentation === "object"
        ? {
            monitor:  parsed.meatDocumentation.monitor  || "",
            evaluate: parsed.meatDocumentation.evaluate || "",
            assess:   parsed.meatDocumentation.assess   || "",
            treat:    parsed.meatDocumentation.treat    || "",
          }
        : null,
      hpiAddition:      typeof parsed.hpiAddition === "string"      ? parsed.hpiAddition      : "",
      assessment:       typeof parsed.assessment === "string"       ? parsed.assessment       : "",
      plan:             Array.isArray(parsed.plan)                  ? parsed.plan             : [],
      icd10Specificity: typeof parsed.icd10Specificity === "string" ? parsed.icd10Specificity : "",
    },
    aiMeta: {
      ...approximateAiMeta(data?.usage, data?.model || model),
      latencyMs,
    },
  };
}

// =============================================================
// Endpoint 4 — POST /draft-note
// =============================================================

/**
 * Genera el borrador completo del template seleccionado en el shape
 * EXACTO que consume `buildEhrNotePayload.js` (camelCase).
 *
 * IMPORTANTE: el backend devuelve `meta.templateName` con el string AI
 * (ej. "Telehealth 2.0"). Antes de pasar el draft a `buildEhrNotePayload`
 * el caller debe convertirlo al string AdvancedMD vía
 * `toAdvmdTemplateName(...)`. También debe llamar `stripAiTrace()` para
 * remover el campo `_aiTrace` (no se envía a AdvancedMD).
 *
 * @param {Object} args
 * @param {string} args.cipId
 * @param {string} args.advancedMdPatientId
 * @param {string} args.templateName                    - AdvancedMD name (se mapea)
 * @param {number[]} args.selectedCareGapIds
 * @param {number[]} args.selectedCodingOpportunityIds
 * @param {Object} [args.doctorContext]                 - { chiefComplaint, freeFormNotes }
 * @param {Object} [args.carryFromLatest]               - { vitals, medications, pmh } (booleans)
 * @param {Object} [args.clinicalContext]
 *
 * Respuesta exitosa (200) — shape compatible con `buildEhrNotePayload`:
 * {
 *   "patient": { "patientId": "129807", "profileId": "3" },
 *   "meta":    { "templateName": "Annual Wellness 2.0", "comment": "...",
 *                "noteDatetime": "MM/dd/yyyy hh:mm:ss AM/PM",
 *                "msgtime":      "MM/dd/yyyy hh:mm:ss AM/PM", "nocookie": "0" },
 *   "location": { }, "reason": { "reasonForAppointment": "..." },
 *   "vitals":   { "weight", "height", "bmi", "temperature", "pulse", "o2SatRa", ... },
 *   "hpi":      { "general": "...", "tcm": "" },
 *   "meds":     { "allReviewedAndReconciled": true, "text": "..." },
 *   "pmh":      { "noChangeReviewed": false, "text": "..." },
 *   "ros":      { }, "exam": { },
 *   "phq2":     { "q1": 0, "q2": 0, "reviewedPhq9": 1 },
 *   "assessPlanFu": { "assessments": "...", "treatment": "...", "followUp": "..." },
 *   "_aiTrace": { "draftSource": "ai-generated", "usedCareGapIds": [...],
 *                 "usedCodingOpportunityIds": [...], "model": "gpt-4o-mini",
 *                 "totalTokens": 4210, "generatedAt": "2026-05-13T18:10:00Z" }
 * }
 */
export async function draftNote({
  cipId,
  advancedMdPatientId,
  templateName,
  selectedCareGapIds = [],
  selectedCodingOpportunityIds = [],
  doctorContext,
  carryFromLatest,
  clinicalContext,
}) {
  if (!cipId) throw new Error("cipId is required");
  if (!advancedMdPatientId) throw new Error("advancedMdPatientId is required");
  if (!templateName) throw new Error("templateName is required");

  try {
    const res = await axios.post(
      `${API_URL}/draft-note`,
      {
        cipId,
        advancedMdPatientId,
        templateName: toAiTemplateName(templateName),
        selectedCareGapIds,
        selectedCodingOpportunityIds,
        doctorContext: doctorContext || undefined,
        carryFromLatest: carryFromLatest || undefined,
        clinicalContext: sanitizeClinicalContext(clinicalContext),
      },
      { headers: { ...authHeader(), "Content-Type": "application/json" } }
    );
    return res.data;
  } catch (err) {
    normalizeError(err);
  }
}

// =============================================================
// Endpoint 5 — POST /link-note
// =============================================================

/**
 * Audita la relación (noteId AdvancedMD) ↔ (care gaps abordados) ↔
 * (coding opps abordadas) ↔ (sugerencias aceptadas / editadas).
 *
 * Estado v1 backend: SOLO contrato. Valida body y devuelve 200 OK con
 * `{ result: 0, message: "Note linked." }` pero NO persiste. Se llama
 * fire-and-forget desde el front; cuando el backend agregue persistencia
 * el front no necesita cambiar.
 *
 * @param {Object} args
 * @param {string} args.cipId
 * @param {string} args.advancedMdNoteId
 * @param {string} args.templateName               - AdvancedMD name (se mapea)
 * @param {Array<{careGapId:number, aiSuggestionAccepted:boolean, aiSuggestionEditedByDoctor:boolean, finalTextSnippet?:string}>} args.linkedCareGaps
 * @param {Array<{codingOpportunityId:number, aiSuggestionAccepted:boolean, examsOrdered?:string[], finalIcd10?:string}>} args.linkedCodingOpportunities
 */
export async function linkNote({
  cipId,
  advancedMdNoteId,
  templateName,
  linkedCareGaps = [],
  linkedCodingOpportunities = [],
}) {
  if (!cipId) throw new Error("cipId is required");
  if (!advancedMdNoteId) throw new Error("advancedMdNoteId is required");

  try {
    const res = await axios.post(
      `${API_URL}/link-note`,
      {
        cipId,
        advancedMdNoteId,
        templateName: toAiTemplateName(templateName),
        linkedCareGaps,
        linkedCodingOpportunities,
      },
      { headers: { ...authHeader(), "Content-Type": "application/json" } }
    );
    return res.data;
  } catch (err) {
    normalizeError(err);
  }
}

// =============================================================
// Health check (reutilizado de IACarePlan)
// =============================================================

/**
 * Verifica que el backend AI esté operativo (API key OpenAI configurada
 * + conectividad). Útil para gatear la apertura del módulo v2.
 *
 * Respuesta exitosa (200):
 * { "ok": true, "model": "gpt-4o-mini", "openAiReachable": true }
 */
export async function checkAiHealth() {
  const res = await axios.get(`${API_BASE_URL}/api/IACarePlan/health`, {
    headers: authHeader(),
  });
  return res.data;
}
